import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { validateInput } from './validation';

type FormStateValue = {
  value?: any;
  rules?: { [key: string]: string | number | boolean };
  isTouched?: boolean;
  error?: string;
  isDependent?: {
    dependentValue: any;
    fieldName: string;
    rules: { [key: string]: string | number | boolean };
  };
  [key: string]: any; // Allow additional properties
};

type FormState = {
  [key: string]: FormStateValue | { [key: string]: any };
};

type InputValue = string | number | boolean | string[] | ChangeEvent<HTMLInputElement> | undefined | null | FileList;

/**
 * Factory function to create an inputChangedHandler for form state management
 * @param setState - React state setter function
 * @param currentState - Current form state object containing rules
 * @param customValidation - Optional custom validation function that can override default validation
 * @returns Handler function that validates input and updates state
 */
export function createInputChangedHandler<T extends Record<string, any>>(
  setState: Dispatch<SetStateAction<T>>,
  currentState: T,
  customValidation?: (value: InputValue, name: string, state: T) => ReturnType<typeof validateInput> | null
): (value: InputValue, name: string) => void {
  return (value: InputValue, name: string) => {
    let inputValidation: ReturnType<typeof validateInput> | null = null;

    const fieldState = currentState[name] as FormStateValue | undefined;
    const rules = fieldState?.rules || {};

    // Use custom validation if provided
    if (customValidation) {
      const customResult = customValidation(value, name, currentState);
      if (customResult !== null) {
        inputValidation = customResult;
      } else {
        // Fall back to default validation
        inputValidation = validateInput(rules, value);
      }
    } else {
      // Default validation with support for dependent fields
      inputValidation = validateInput(rules, value);

      // Check for dependent field validation (like BFIP Tenancy)
      if (fieldState?.isDependent) {
        const dependentField = fieldState.isDependent;
        const dependentFieldState = currentState[dependentField.fieldName] as FormStateValue | undefined;
        if (dependentField.dependentValue === dependentFieldState?.value) {
          inputValidation = validateInput(dependentField.rules, value);
        }
      }
    }

    setState((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        value,
        isTouched: true,
        error: inputValidation.error,
      },
    }));
  };
}
